import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import DOMPurify from 'dompurify'
import {
    MdAdd,
    MdArrowBack,
    MdClose,
    MdDelete,
    MdEdit,
    MdFilterList,
    MdLink,
    MdMenuBook,
    MdSearch,
} from 'react-icons/md'
import { FaWhatsapp } from 'react-icons/fa'
import RichTextEditor from '@/components/rich-text-editor'
import ConfirmModal from '@/components/confirm-modal'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    getSystemModules,
    type SystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import {
    createArticle,
    deleteArticle,
    getArticle,
    getArticles,
    updateArticle,
    uploadHelpImage,
    type HelpArticle,
    type HelpCenterCapabilities,
} from '@/lib/api/help-center/help-center-api'

async function handleEditorImageUpload(file: File): Promise<string> {
    const res = await uploadHelpImage(file)
    if (res.success && res.result?.url) return res.result.url
    toast.error(res.error || 'Não foi possível enviar a imagem')
    throw new Error(res.error || 'upload-failed')
}

const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString('pt-BR') : ''

interface Props {
    capabilities: HelpCenterCapabilities
}

type View = 'list' | 'view' | 'edit' | 'new'

export default function ArticlesTab({ capabilities }: Props) {
    const [searchParams, setSearchParams] = useSearchParams()
    const [view, setView] = useState<View>('list')

    const [articles, setArticles] = useState<HelpArticle[]>([])
    const [loadingList, setLoadingList] = useState(true)
    const [search, setSearch] = useState('')

    const [modules, setModules] = useState<SystemModule[]>([])
    const [filterModuleKey, setFilterModuleKey] = useState('')
    const [filterResourceKey, setFilterResourceKey] = useState('')
    const [menuOpen, setMenuOpen] = useState(false)

    const { canAccessModule, canRead } = usePermission()

    const [selected, setSelected] = useState<HelpArticle | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)

    // Form
    const [formTitle, setFormTitle] = useState('')
    const [formContent, setFormContent] = useState('')
    const [formModuleKey, setFormModuleKey] = useState('')
    const [formResourceKey, setFormResourceKey] = useState('')
    const [formPublished, setFormPublished] = useState(true)
    const [saving, setSaving] = useState(false)

    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)

    // Lightbox de imagem ao clicar numa imagem do artigo
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

    const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement
        if (target.tagName === 'IMG') {
            const src = (target as HTMLImageElement).currentSrc || (target as HTMLImageElement).src
            if (src) setLightboxSrc(src)
        }
    }

    const canManage =
        capabilities.canCreateArticles || capabilities.canUpdateArticles

    const moduleLabel = useCallback(
        (moduleKey?: string | null, resourceKey?: string | null) => {
            if (!moduleKey) return ''
            const mod = modules.find(m => m.key === moduleKey)
            const res = mod?.resources.find(r => r.key === resourceKey)
            const modName = mod?.label || moduleKey
            return res ? `${modName} · ${res.label}` : modName
        },
        [modules]
    )

    const loadList = useCallback(async () => {
        setLoadingList(true)
        try {
            const res = await getArticles({
                search: search.trim() || undefined,
                moduleKey: filterModuleKey || undefined,
                resourceKey: filterResourceKey || undefined,
            })
            if (res.success && res.result) setArticles(res.result)
        } finally {
            setLoadingList(false)
        }
    }, [search, filterModuleKey, filterResourceKey])

    // Menu de navegacao: apenas modulos/recursos que o usuario logado acessa
    // (mesmo controle do AdminSidebar via usePermission).
    const visibleModules = useMemo(
        () =>
            modules
                .filter(m => canAccessModule(m.key))
                .map(m => ({
                    ...m,
                    resources: (m.resources || []).filter(r =>
                        canRead(m.key, r.key),
                    ),
                })),
        [modules, canAccessModule, canRead],
    )

    const selectModule = (moduleKey: string) => {
        setFilterModuleKey(moduleKey)
        setFilterResourceKey('')
    }

    const selectResource = (moduleKey: string, resourceKey: string) => {
        setFilterModuleKey(moduleKey)
        setFilterResourceKey(resourceKey)
        setMenuOpen(false)
    }

    const clearFilter = () => {
        setFilterModuleKey('')
        setFilterResourceKey('')
        setMenuOpen(false)
    }

    const openView = useCallback(async (id: number) => {
        setView('view')
        setLoadingDetail(true)
        try {
            const res = await getArticle(id)
            if (res.success && res.result) {
                setSelected(res.result)
            } else {
                toast.error(res.error || 'Não foi possível abrir o artigo')
                setView('list')
            }
        } finally {
            setLoadingDetail(false)
        }
    }, [])

    useEffect(() => {
        getSystemModules()
            .then(res => {
                if (res.success && res.result) setModules(res.result)
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (view === 'list') loadList()
    }, [view, loadList])

    // Deep link ?article=ID
    useEffect(() => {
        const articleId = searchParams.get('article')
        if (articleId) openView(Number(articleId))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const resourcesForModule = useMemo(
        () => modules.find(m => m.key === formModuleKey)?.resources ?? [],
        [modules, formModuleKey]
    )

    const startNew = () => {
        setSelected(null)
        setFormTitle('')
        setFormContent('')
        setFormModuleKey('')
        setFormResourceKey('')
        setFormPublished(true)
        setView('new')
    }

    const startEdit = (article: HelpArticle) => {
        setSelected(article)
        setFormTitle(article.title)
        setFormContent(article.content)
        setFormModuleKey(article.moduleKey || '')
        setFormResourceKey(article.resourceKey || '')
        setFormPublished(article.published)
        setView('edit')
    }

    const handleSave = async () => {
        if (!formTitle.trim()) {
            toast.warning('Informe um título')
            return
        }
        if (!formContent.trim() || formContent.trim() === '<p></p>') {
            toast.warning('Escreva o conteúdo do artigo')
            return
        }
        setSaving(true)
        try {
            const payload = {
                title: formTitle.trim(),
                content: formContent,
                moduleKey: formModuleKey || undefined,
                resourceKey: formResourceKey || undefined,
                published: formPublished,
            }
            const res =
                view === 'edit' && selected
                    ? await updateArticle(selected.id, payload)
                    : await createArticle(payload)
            if (res.success && res.result) {
                toast.success(
                    view === 'edit' ? 'Artigo atualizado' : 'Artigo publicado'
                )
                setSelected(res.result)
                setView('view')
            } else {
                toast.error(res.error || 'Não foi possível salvar o artigo')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!selected) return
        setDeleting(true)
        try {
            const res = await deleteArticle(selected.id)
            if (res.success) {
                toast.success('Artigo excluído')
                setConfirmDelete(false)
                backToList()
            } else {
                toast.error(res.error || 'Não foi possível excluir')
            }
        } finally {
            setDeleting(false)
        }
    }

    const backToList = () => {
        setSelected(null)
        setView('list')
        const next = new URLSearchParams(searchParams)
        next.delete('article')
        setSearchParams(next, { replace: true })
    }

    const articleUrl = (id: number) =>
        `${window.location.origin}/support?tab=articles&article=${id}`

    const handleCopyLink = async () => {
        if (!selected) return
        const url = articleUrl(selected.id)
        try {
            await navigator.clipboard.writeText(url)
            toast.success('Link copiado para a área de transferência')
        } catch {
            // fallback quando clipboard nao esta disponivel (http/permissoes)
            window.prompt('Copie o link do artigo:', url)
        }
    }

    const handleShareWhatsApp = () => {
        if (!selected) return
        const text = `${selected.title}\n${articleUrl(selected.id)}`
        window.open(
            `https://wa.me/?text=${encodeURIComponent(text)}`,
            '_blank',
            'noopener,noreferrer',
        )
    }

    // ===== FORM (novo/editar) =====
    if (view === 'new' || view === 'edit') {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={() => setView(selected ? 'view' : 'list')}
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <h2 className="text-lg font-semibold">
                        {view === 'edit' ? 'Editar artigo' : 'Novo artigo'}
                    </h2>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 flex flex-col gap-4">
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">
                                Título *
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered"
                            value={formTitle}
                            onChange={e => setFormTitle(e.target.value)}
                            placeholder="Ex: Como emitir uma guia"
                            maxLength={200}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Módulo
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formModuleKey}
                                onChange={e => {
                                    setFormModuleKey(e.target.value)
                                    setFormResourceKey('')
                                }}
                            >
                                <option value="">Nenhum (geral)</option>
                                {modules.map(m => (
                                    <option key={m.key} value={m.key}>
                                        {m.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Recurso
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formResourceKey}
                                onChange={e =>
                                    setFormResourceKey(e.target.value)
                                }
                                disabled={!formModuleKey}
                            >
                                <option value="">Todos os recursos</option>
                                {resourcesForModule.map(r => (
                                    <option key={r.key} value={r.key}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium">
                                Conteúdo *
                            </span>
                        </label>
                        <RichTextEditor
                            content={formContent}
                            onChange={setFormContent}
                            placeholder="Escreva o tutorial ou artigo..."
                            minHeight="320px"
                            onImageUpload={handleEditorImageUpload}
                        />
                    </div>

                    <label className="label cursor-pointer justify-start gap-3">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-secondary"
                            checked={formPublished}
                            onChange={e => setFormPublished(e.target.checked)}
                        />
                        <span className="label-text">
                            Publicado (visível para todos os usuários)
                        </span>
                    </label>

                    <div className="flex items-center justify-end gap-3">
                        <button
                            className="btn btn-ghost"
                            onClick={() => setView(selected ? 'view' : 'list')}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving && (
                                <span className="loading loading-spinner loading-sm" />
                            )}
                            {view === 'edit'
                                ? 'Salvar alterações'
                                : 'Publicar artigo'}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // ===== VISUALIZAR =====
    if (view === 'view') {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between gap-2">
                    <button
                        className="btn btn-ghost btn-sm gap-2"
                        onClick={backToList}
                    >
                        <MdArrowBack className="text-xl" /> Voltar
                    </button>
                    {selected && (
                        <div className="flex items-center gap-2">
                            <button
                                className="btn btn-ghost btn-sm gap-2"
                                onClick={handleCopyLink}
                                title="Copiar link do artigo"
                            >
                                <MdLink className="text-lg" />
                                <span className="hidden sm:inline">
                                    Copiar link
                                </span>
                            </button>
                            <button
                                className="btn btn-ghost btn-sm gap-2 text-success"
                                onClick={handleShareWhatsApp}
                                title="Compartilhar no WhatsApp"
                            >
                                <FaWhatsapp className="text-lg" />
                            </button>
                            {capabilities.canUpdateArticles && (
                                <button
                                    className="btn btn-ghost btn-sm gap-2"
                                    onClick={() => startEdit(selected)}
                                >
                                    <MdEdit /> Editar
                                </button>
                            )}
                            {capabilities.canDeleteArticles && (
                                <button
                                    className="btn btn-ghost btn-sm text-error"
                                    onClick={() => setConfirmDelete(true)}
                                >
                                    <MdDelete className="text-lg" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {loadingDetail || !selected ? (
                    <div className="flex justify-center py-20">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : (
                    <article className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 md:p-8">
                        {selected.moduleKey && (
                            <span className="badge badge-ghost mb-3">
                                {moduleLabel(
                                    selected.moduleKey,
                                    selected.resourceKey
                                )}
                            </span>
                        )}
                        <h1 className="text-2xl font-bold text-base-content mb-1">
                            {selected.title}
                        </h1>
                        <p className="text-sm text-base-content/50 mb-6">
                            {selected.authorName} ·{' '}
                            {formatDate(selected.updatedAt)}
                            {!selected.published && (
                                <span className="badge badge-warning badge-sm ml-2">
                                    Rascunho
                                </span>
                            )}
                        </p>
                        <div
                            className="prose prose-sm md:prose-base max-w-none [&_img]:cursor-zoom-in [&_img]:rounded-lg [&_p:empty]:min-h-[1em]"
                            onClick={handleContentClick}
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(selected.content),
                            }}
                        />
                    </article>
                )}

                <ConfirmModal
                    isOpen={confirmDelete}
                    onClose={() => setConfirmDelete(false)}
                    onConfirm={handleDelete}
                    title="Excluir artigo"
                    message="Tem certeza de que deseja excluir este artigo? Esta ação não pode ser desfeita."
                    confirmButtonText="Excluir"
                    cancelButtonText="Cancelar"
                    loading={deleting}
                />

                {lightboxSrc && (
                    <div
                        className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
                        onClick={() => setLightboxSrc(null)}
                    >
                        <img
                            src={lightboxSrc}
                            alt="Imagem do artigo"
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            onClick={e => e.stopPropagation()}
                        />
                        <button
                            className="absolute top-4 right-4 btn btn-circle btn-ghost text-white"
                            onClick={() => setLightboxSrc(null)}
                        >
                            <MdClose className="text-2xl" />
                        </button>
                    </div>
                )}
            </div>
        )
    }

    // ===== LISTA =====
    const noFilter = !filterModuleKey && !filterResourceKey

    const menuList = (
        <ul className="menu bg-base-100 rounded-xl border border-base-300/50 p-2 w-full">
                    <li>
                        <button
                            className={noFilter ? 'active' : ''}
                            onClick={clearFilter}
                        >
                            <MdMenuBook className="text-lg" /> Todos os artigos
                        </button>
                    </li>
                    {visibleModules.map(m => (
                        <li key={m.key}>
                            <details open={filterModuleKey === m.key}>
                                <summary
                                    className={
                                        filterModuleKey === m.key &&
                                        !filterResourceKey
                                            ? 'active font-medium'
                                            : ''
                                    }
                                    onClick={e => {
                                        // controle total pelo estado: evita o toggle
                                        // nativo do <details> (que exigia 2 cliques)
                                        e.preventDefault()
                                        selectModule(m.key)
                                    }}
                                >
                                    {m.label}
                                </summary>
                                {m.resources.length > 0 && (
                                    <ul>
                                        {m.resources.map(r => (
                                            <li key={r.key}>
                                                <button
                                                    className={
                                                        filterModuleKey ===
                                                            m.key &&
                                                        filterResourceKey ===
                                                            r.key
                                                            ? 'active'
                                                            : ''
                                                    }
                                                    onClick={() =>
                                                        selectResource(
                                                            m.key,
                                                            r.key,
                                                        )
                                                    }
                                                >
                                                    {r.label}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </details>
                        </li>
                    ))}
                </ul>
    )

    return (
        <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* Menu de modulos/recursos — coluna fixa no desktop */}
            <aside className="hidden lg:block w-64 shrink-0">{menuList}</aside>

            {/* Drawer no mobile (igual ao sidebar com hamburguer) */}
            {menuOpen && (
                <div className="lg:hidden fixed inset-0 z-50">
                    <div
                        className="fixed inset-0 bg-black/50"
                        onClick={() => setMenuOpen(false)}
                    />
                    <div className="fixed left-0 top-0 bottom-0 z-50 w-72 max-w-[80%] bg-base-100 shadow-2xl overflow-y-auto p-3">
                        <div className="flex items-center justify-between mb-2 px-1">
                            <span className="font-semibold text-base-content">
                                Categorias
                            </span>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setMenuOpen(false)}
                            >
                                <MdClose className="text-xl" />
                            </button>
                        </div>
                        {menuList}
                    </div>
                </div>
            )}

            {/* Conteudo: busca + lista */}
            <div className="flex-1 w-full flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <button
                        className="btn btn-ghost btn-sm gap-2 lg:hidden"
                        onClick={() => setMenuOpen(true)}
                    >
                        <MdFilterList className="text-lg" /> Categorias
                    </button>
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 text-lg z-10" />
                        <input
                            type="text"
                            className="input input-bordered w-full pl-10"
                            placeholder="Buscar artigos e tutoriais..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && loadList()}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="btn btn-ghost" onClick={loadList}>
                            Buscar
                        </button>
                        {capabilities.canCreateArticles && (
                            <button
                                className="btn btn-secondary gap-2"
                                onClick={startNew}
                            >
                                <MdAdd className="text-lg" /> Novo artigo
                            </button>
                        )}
                    </div>
                </div>

                {!noFilter && (
                    <div className="flex items-center gap-2 text-sm text-base-content/60">
                        <span>Filtrando por:</span>
                        <span className="badge badge-secondary badge-outline gap-1">
                            {moduleLabel(filterModuleKey, filterResourceKey)}
                        </span>
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={clearFilter}
                        >
                            Limpar
                        </button>
                    </div>
                )}

                {loadingList ? (
                    <div className="flex justify-center py-20">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : articles.length === 0 ? (
                    <div className="bg-base-100 rounded-xl border border-dashed border-base-300 p-12 text-center text-base-content/60">
                        <MdMenuBook className="text-4xl mx-auto mb-3 opacity-40" />
                        <p>Nenhum artigo encontrado.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                        {articles.map(article => (
                            <button
                                key={article.id}
                                className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-5 text-left hover:border-secondary/50 transition-colors flex flex-col gap-2"
                                onClick={() => openView(article.id)}
                            >
                                <div className="flex items-start gap-2">
                                    <MdMenuBook className="text-secondary text-xl mt-0.5 shrink-0" />
                                    <span className="font-semibold text-base-content">
                                        {article.title}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    {article.moduleKey && (
                                        <span className="badge badge-ghost badge-sm">
                                            {moduleLabel(
                                                article.moduleKey,
                                                article.resourceKey,
                                            )}
                                        </span>
                                    )}
                                    {!article.published && (
                                        <span className="badge badge-warning badge-sm">
                                            Rascunho
                                        </span>
                                    )}
                                    <span className="text-xs text-base-content/50 ml-auto">
                                        {formatDate(article.updatedAt)}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
